// Tiny typed fetch wrapper. Intentionally minimal: no interceptor stack, no
// retries (TanStack Query owns that), no auto-refresh (the auth flow is a
// full-page redirect). It throws `ApiError` on non-2xx responses so callers
// — including TanStack Query — can branch on .status.

export const API_BASE: string =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE) || '/api';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** Will be JSON.stringified if not already a string/FormData/etc. */
  body?: unknown;
  /** Adds ?key=value pairs; undefined/null entries are dropped. */
  query?: Record<string, string | number | boolean | null | undefined>;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const base = path.startsWith('http') ? path : `${API_BASE}${path}`;
  if (!query) return base;
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    usp.set(k, String(v));
  }
  const qs = usp.toString();
  return qs ? `${base}${base.includes('?') ? '&' : '?'}${qs}` : base;
}

function shouldSerialize(body: unknown): body is object {
  if (body === undefined || body === null) return false;
  if (typeof body === 'string') return false;
  if (typeof FormData !== 'undefined' && body instanceof FormData) return false;
  if (typeof Blob !== 'undefined' && body instanceof Blob) return false;
  if (body instanceof ArrayBuffer) return false;
  return true;
}

export async function apiFetch<T = unknown>(
  path: string,
  { body, query, headers, ...init }: RequestOptions = {},
): Promise<T> {
  const url = buildUrl(path, query);
  const reqHeaders = new Headers(headers);
  let requestBody: BodyInit | undefined;
  if (shouldSerialize(body)) {
    reqHeaders.set('Content-Type', 'application/json');
    requestBody = JSON.stringify(body);
  } else if (body !== undefined && body !== null) {
    requestBody = body as BodyInit;
  }
  reqHeaders.set('Accept', 'application/json');

  const response = await fetch(url, {
    ...init,
    credentials: 'include',
    headers: reqHeaders,
    body: requestBody,
  });

  // 204 / 205 → no body.
  if (response.status === 204 || response.status === 205) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  const payload: unknown = isJson
    ? await response.json().catch(() => null)
    : await response.text().catch(() => '');

  if (!response.ok) {
    const detail =
      (payload && typeof payload === 'object' && 'detail' in payload && payload.detail) ||
      (typeof payload === 'string' && payload) ||
      response.statusText;
    throw new ApiError(`HTTP ${response.status}: ${detail}`, response.status, payload);
  }

  return payload as T;
}

export function isUnauthorized(err: unknown): err is ApiError {
  return err instanceof ApiError && err.status === 401;
}

export function isForbidden(err: unknown): err is ApiError {
  return err instanceof ApiError && err.status === 403;
}
